"use client";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { TOGGLE_GALLERY_APPROVAL } from "api";
import { fetch } from "libs";

export const toggleGalleryApproval = async (imageId: string) => {
  const response = await fetch(TOGGLE_GALLERY_APPROVAL(imageId), {
    method: "PATCH",
  });

  if (!response.ok) {
    throw new Error("Failed to toggle gallery approval status");
  }

  return response.json(); // Return the updated image data
};

const useToggleGalleryApproval = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (imageId: string) => toggleGalleryApproval(imageId),

    onMutate: async (imageId) => {
      await queryClient.cancelQueries({ queryKey: ["image-generations"] });

      const previousData = queryClient.getQueryData(["image-generations"]);

      queryClient.setQueryData(["image-generations"], (oldData: any) => {
        if (!oldData) return oldData;

        const updatedPages = oldData.pages.map((page: any) => ({
          ...page,
          image_generations: page.image_generations.map((image: any) =>
            image._id === imageId
              ? {
                  ...image,
                  approved_for_gallery: !image.approved_for_gallery,
                }
              : image
          ),
        }));

        return { ...oldData, pages: updatedPages };
      });

      return { previousData };
    },

    onError: (_error, _variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(["image-generations"], context.previousData);
      }
    },

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["image-generations"] });
    },
  });
};

export default useToggleGalleryApproval;
